import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { MAINTENANCE_TOOLTIP, useWritesPaused } from '@context/maintenance';

type Variant = 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link';
type Size = 'default' | 'sm' | 'lg' | 'icon';

const VARIANT: Record<Variant, string> = {
  default: 'bg-blue-600 text-white shadow hover:bg-blue-800',
  outline: 'border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50',
  secondary: 'border border-blue-600 bg-white text-blue-600 shadow-sm hover:bg-blue-50',
  ghost: 'text-gray-600 hover:bg-blue-50 hover:text-gray-900',
  destructive: 'bg-rose-600 text-white shadow-sm hover:bg-rose-700',
  link: 'text-blue-600 underline-offset-4 hover:underline',
};

const SIZE: Record<Size, string> = {
  default: 'h-9 px-4 py-2',
  sm: 'h-8 rounded-md px-3 text-xs',
  lg: 'h-12 rounded-md px-8 text-lg',
  icon: 'h-9 w-9',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  /** Changes or adds data: disabled while scheduled maintenance pauses changes (#3561). */
  writes?: boolean;
}

export function Button({ variant = 'default', size = 'default', className, children, writes, disabled, title, ...rest }: Props) {
  const paused = useWritesPaused() && !!writes;
  return (
    <button
      disabled={disabled || paused}
      title={paused ? MAINTENANCE_TOOLTIP : title}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors [&_svg]:h-4 [&_svg]:w-4 disabled:pointer-events-none disabled:opacity-60',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
